import { useMemo, useState } from "react";
import {
  Copy,
  Mail,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VBadge,
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuSeparator,
  VDropdownMenuTrigger,
  VInput,
  cn,
} from "@/components/ui";
import { useDeleteTemplate } from "@/domains/communication/api/mutations";
import { useTemplates } from "@/domains/communication/api/queries";
import type {
  EmailTemplate,
  EmailTemplateAudience,
} from "@/domains/communication/api/types";
import {
  TemplateFormDialog,
  type TemplateFormMode,
} from "@/domains/communication/components/templateFormDialog";

const AUDIENCE_LABEL: Record<EmailTemplateAudience, string> = {
  requester: "Aanvrager",
  partner: "Plaatsingsfirma",
  internal: "Intern",
};

const AUDIENCE_COLOR: Record<EmailTemplateAudience, React.ComponentProps<typeof VBadge>["color"]> =
  {
    requester: "info",
    partner: "warning",
    internal: "neutral",
  };

type AudienceFilter = EmailTemplateAudience | "all";

const FILTER_OPTIONS: ReadonlyArray<{ value: AudienceFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "requester", label: "Aanvrager" },
  { value: "partner", label: "Plaatsingsfirma" },
  { value: "internal", label: "Intern" },
];

export function TemplateListPage(): React.JSX.Element {
  const { data, isPending, isError } = useTemplates();
  const deleteTemplate = useDeleteTemplate();
  const [formMode, setFormMode] = useState<TemplateFormMode | null>(null);
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState<AudienceFilter>("all");

  const filtered = useMemo(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((tpl) => {
      if (audience !== "all" && tpl.audience !== audience) return false;
      if (!q) return true;
      return (
        tpl.name.toLowerCase().includes(q) ||
        tpl.subject.toLowerCase().includes(q) ||
        tpl.body.toLowerCase().includes(q)
      );
    });
  }, [data, search, audience]);

  function handleDelete(tpl: EmailTemplate): void {
    const ok = window.confirm(`Template "${tpl.name}" verwijderen?`);
    if (!ok) return;
    deleteTemplate.mutate(tpl.id);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="E-mail templates"
        subtitle="Beheer de templates die automatisch worden gebruikt bij communicatie met aanvragers en externe partners."
        actions={
          <VButton onClick={() => setFormMode({ kind: "create" })}>
            <Plus className="size-4" aria-hidden />
            Nieuwe template
          </VButton>
        }
      />

      <Card padding="md">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative md:w-80">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <VInput
              placeholder="Zoek op naam, onderwerp of inhoud…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTER_OPTIONS.map((opt) => {
              const active = audience === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={cn(
                    "cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-primary-600 bg-primary-600 text-on-primary"
                      : "border-border bg-card text-muted-foreground hover:border-primary-300 hover:text-foreground",
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {isPending ? (
        <LoadingState label="Templates laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon templates niet ophalen." />
      ) : filtered.length === 0 ? (
        (data ?? []).length === 0 ? (
          <EmptyState
            title="Geen templates"
            description="Maak je eerste template aan om communicatie te standaardiseren."
            action={
              <VButton onClick={() => setFormMode({ kind: "create" })}>
                <Plus className="size-4" aria-hidden />
                Nieuwe template
              </VButton>
            }
          />
        ) : (
          <EmptyState
            title="Geen resultaten"
            description="Geen templates komen overeen met je zoekopdracht of filter."
          />
        )
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((tpl) => (
            <Card key={tpl.id} padding="lg" className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="mt-0.5 inline-flex size-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <Mail className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <CardTitle className="truncate">{tpl.name}</CardTitle>
                    <Caption tone="muted" className="line-clamp-1">
                      {tpl.subject}
                    </Caption>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <VBadge color={AUDIENCE_COLOR[tpl.audience]}>
                    {AUDIENCE_LABEL[tpl.audience]}
                  </VBadge>
                  <VDropdownMenu>
                    <VDropdownMenuTrigger asChild>
                      <VButton
                        variant="ghost"
                        size="icon"
                        aria-label="Acties"
                        className="size-8"
                        icon={<MoreVertical className="size-4" aria-hidden />}
                      />
                    </VDropdownMenuTrigger>
                    <VDropdownMenuContent align="end">
                      <VDropdownMenuItem onSelect={() => setFormMode({ kind: "edit", template: tpl })}>
                        <Pencil className="size-4" aria-hidden />
                        Bewerken
                      </VDropdownMenuItem>
                      <VDropdownMenuItem
                        onSelect={() => setFormMode({ kind: "duplicate", source: tpl })}
                      >
                        <Copy className="size-4" aria-hidden />
                        Dupliceren
                      </VDropdownMenuItem>
                      <VDropdownMenuSeparator />
                      <VDropdownMenuItem
                        onSelect={() => handleDelete(tpl)}
                        className="text-error-700 focus:bg-error-50 focus:text-error-700"
                      >
                        <Trash2 className="size-4" aria-hidden />
                        Verwijderen
                      </VDropdownMenuItem>
                    </VDropdownMenuContent>
                  </VDropdownMenu>
                </div>
              </div>

              <pre className="flex-1 max-h-32 overflow-hidden whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                {tpl.body.slice(0, 240)}
                {tpl.body.length > 240 ? "…" : ""}
              </pre>

              {tpl.variables.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tpl.variables.map((v) => (
                    <VBadge key={v} color="secondary">
                      {`{{${v}}}`}
                    </VBadge>
                  ))}
                </div>
              ) : (
                <Body tone="muted" className="text-xs italic">
                  Geen variabelen — statische tekst.
                </Body>
              )}
            </Card>
          ))}
        </div>
      )}

      {formMode ? (
        <TemplateFormDialog
          open
          onOpenChange={(open) => {
            if (!open) setFormMode(null);
          }}
          mode={formMode}
        />
      ) : null}
    </div>
  );
}
